import apiServices from 'config/apiServiceApiGateway'
import { OptionType } from 'features/CampaignAndDiscount/@types/campaignUmbrellaAddEdit'
import { CampaignChildStatusType } from 'features/CampaignAndDiscount/@types/campaignUmbrellaChild'

const { Get, Post, Delete, Put } = apiServices

export const getCampaignObjection = () =>
  Get<OptionType[]>({ url: `/skp/internal/v1/brand-submissions/campaign-objectives` })

export const getBuyerTarget = () =>
  Get<OptionType[]>({ url: `/skp/internal/v1/brand-submissions/buyer-targets` })

export const getL0Objectives = () =>
  Get<OptionType[]>({ url: `/skp/internal/v1/brand-submissions/l0-objectives` })

export const getL1Objectives = () =>
  Get<OptionType[]>({ url: `/skp/internal/v1/brand-submissions/l1-objectives` })

export const getCampaignTarget = () =>
  Get<OptionType[]>({ url: `/skp/internal/v1/brand-submissions/campaign-targets` })

export const getPackages = () =>
  Get<OptionType[]>({ url: `/skp/internal/v1/brand-submissions/packages` })

export type ObjectTypeIdValueType = {
  id: string
  value: number
}
type ErrorType = {
  status: false
  message: ''
  code: 0
}

export type PostCampaignUmbrellaRequestType = {
  data: {
    campaign_exposures?: string[]
    campaign_targets?: string[]
    campaign_objectives?: string[]
    buyer_targets?: ObjectTypeIdValueType[]
    l0_objectives?: ObjectTypeIdValueType[]
    l1_objectives?: ObjectTypeIdValueType[]
    attachment: string
    campaign_theme: string
    submission_date_start: string
    submission_date_end: string
    campaign_period_start: string
    campaign_period_end: string
    vendor_quota: number
    is_public_campaign: boolean
    promo_criteria: {
      single_discount?: {
        discount_min: number
        quota_min: number
        amount_sku_min: number
      }
      bmsm?: {
        tiers: { min_purchase: number; discount_min: number }[]
        quota_min: number
        amount_sku_min: number
      }
      flash_sale?: {
        quota_min: number
        discount_min: number
        amount_sku_min: number
      }
    }
    project_id?: string
    created_by: string
    month_year: string
    package_id: string
    campaign_cluster_id: number
  }
}

export type GetCampaignClusterResponseType = {
  data: {
    campaign_clusters: {
      cluster: string
      id: number
    }[]
  }
}

export type PostCampaignUmbrellaChildRequestType = {
  data: {
    campaign_name: string
    promotion_type_id: string
    reimbursement_method_id: string // Transfer
    campaign_start_date: string
    campaign_end_date: string
    campaign_cluster_id: number
    vendor_id: number
    brand_submission_id: number
    detail_from_skp_id: number | null
    brand_id: number
    brand_name: string
    is_budget_internal: boolean
    is_topup: boolean
  }
  submission_id: string
}

export type PostCampaignUmbrellaChildResponseType = {
  id: number
  message: string
}

export type PostSKPStatusRequestType = {
  status:
    | 'submit_to_catman'
    | 'submit_to_head'
    | 'approve_as_head'
    | 'approve_as_campaign'
    | 'reject'
    | 'reopen'
    | 'archive'
  skpId: string
}

export type GetCampaignUmbrellaDetailRequestType = {
  brandSubmissionID: string
}

export type BatchType = {
  close_date: string
  status: string
}

export type GetCampaignUmbrellaDetailResponseType = {
  data: {
    campaign_theme: string
    campaign_exposures: string[]
    buyer_targets: ObjectTypeIdValueType[]
    l1_objectives: ObjectTypeIdValueType[]
    l0_objectives: ObjectTypeIdValueType[]

    can_edit: string | boolean
    submission_date_end: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    brand_submission_batches: { '1': BatchType; '2': BatchType }
    is_public_campaign: boolean
    project_id: string
    campaign_objectives: string[]
    campaign_period_start: string
    campaign_period_end: string
    promo_criteria: {
      single_discount: { quota_min: number; discount_min: number; amount_sku_min: number }
      bmsm: {
        quota_min: number
        amount_sku_min: number
        tiers: { min_purchase: number; discount_min: number }[]
      }
      flash_sale: { quota_min: number; discount_min: number; amount_sku_min: number }
    }
    submission_status: string
    submission_date_start: string
    vendor_quota: number
    attachment: string
    campaign_targets: string[]
    month_year: string
    package_id: string
    campaign_cluster_id: number
  }
  error: ErrorType
}

export type CampaignUmbrellaChildItemType = {
  submission_status: string
  vendor_name: string
  status: CampaignChildStatusType
  end_date: string
  campaign_created_date: string
  promotion_type: string
  start_date: string
  skp_id: number
  generated_skp: string
  campaign_name: string
  vendor_approval_date: string
  initial_submitted_date: string
  final_approval_date: string
}

export type GetCampaignUmbrellaChildResponseType = {
  data: CampaignUmbrellaChildItemType[]
  pagination: {
    prev_cursor: string
    next_cursor: string
  }
  error: ErrorType
}

export type GetCampaignUmbrellaChildRequestType = {
  brandSubmissionID: string
  params: {
    vendor_name?: string
    cursor?: string
    page_size: number
    search?: string
  }
}

export type CampaignUmbrellaItemType = {
  can_delete: boolean
  vendor_total: number
  submission_status: string
  submission_date_start: string
  campaign_period_start: string
  vendor_quota: number
  id: number
  campaign_period_end: string
  campaign_theme: string
  can_edit: boolean
  submission_date_end: string
  campaign_exposures: string[]
  // eslint-disable-next-line @typescript-eslint/naming-convention
  brand_submission_batches: { '1': BatchType; '2': BatchType }
}

export type GetCampaignUmbrellaListResponseType = {
  data: CampaignUmbrellaItemType[]
  pagination: {
    page_size: number
    number_of_elements: number
    sort: string
    total_elements: number
    page_index: number
    direction: string
    total_pages: number
  }
  error: ErrorType
}

export type GetCampaignUmbrellaListRequestType = {
  params: {
    page_index: number
    page_size: number
    campaign_theme?: string
    submission_status?: string
    created_at?: string
  }
}

export const getCampaignCluster = () =>
  Get<GetCampaignClusterResponseType, true>({ url: `/skp/internal/v1/campaign-clusters` })

export const postCampaignUmbrellaChild = ({
  data,
  submission_id,
}: PostCampaignUmbrellaChildRequestType) =>
  Post<PostCampaignUmbrellaChildResponseType, true>({
    url: `/skp/internal/v1/brand-submissions/${submission_id}/childs`,
    data,
  })

export const getCampaignUmbrellaChild = ({
  brandSubmissionID,
  params,
}: GetCampaignUmbrellaChildRequestType) =>
  Get<GetCampaignUmbrellaChildResponseType, true>({
    url: `/skp/internal/v1/brand-submissions/${brandSubmissionID}/childs`,
    params,
  })

export const postSKPStatus = ({ skpId, status }: PostSKPStatusRequestType) =>
  Post<{ message: string }, true>({
    url: `/skp/internal/v3/skps/${skpId}/status/${status}`,
  })

export const getCampaignUmbrellaDetail = ({
  brandSubmissionID,
}: GetCampaignUmbrellaDetailRequestType) =>
  Get<GetCampaignUmbrellaDetailResponseType, true>({
    url: `/skp/internal/v1/brand-submissions/${brandSubmissionID}`,
  })

export const getCampaignUmbrellaList = ({ params }: GetCampaignUmbrellaListRequestType) =>
  Get<GetCampaignUmbrellaListResponseType, true>({
    url: `/skp/internal/v1/brand-submissions`,
    params,
  })

export const deleteCampaignUmbrella = ({ brandSubmissionID }: { brandSubmissionID: string }) =>
  Delete<{ message: string; error: ErrorType }, true>({
    url: `/skp/internal/v1/brand-submissions/${brandSubmissionID}`,
  })

export const deleteCampaignUmbrellaChild = ({ skpId }: { skpId: string }) =>
  Delete<{ message: string; error: ErrorType }, true>({ url: `/skp/internal/v2/skp/${skpId}` })

export const postCampaignUmbrella = ({ data }: PostCampaignUmbrellaRequestType) =>
  Post<{ message: string; id: number; error: ErrorType }, true>({
    url: `/skp/internal/v1/brand-submissions`,
    data,
  })

export type PutCampaignUmbrellaRequestType = {
  data: PostCampaignUmbrellaRequestType['data']
  brandSubmissionID: string
}

export const putCampaignUmbrella = ({ data, brandSubmissionID }: PutCampaignUmbrellaRequestType) =>
  Put<{ message: string; id: number; error: ErrorType }, true>({
    url: `/skp/internal/v1/brand-submissions/${brandSubmissionID}`,
    data,
  })

export type PutCampaignUmbrellaChildRequestType = {
  skpId: string
  brandSubmissionId: string
  data: PostCampaignUmbrellaChildRequestType['data']
}

export const putCampaignUmbrellaChild = ({
  skpId,
  brandSubmissionId,
  data,
}: PutCampaignUmbrellaChildRequestType) =>
  Put<{ message: string }, true>({
    url: `/skp/internal/v1/brand-submissions/${brandSubmissionId}/childs/${skpId}`,
    data,
  })

export type DeleteInventoryByIdResponseType = {
  message: string
}

export type DeleteInventoryByIdRequestType = {
  params: { id: string }
  skpId: string
}

export const deleteInventoryById = ({ params, skpId }: DeleteInventoryByIdRequestType) =>
  Delete<DeleteInventoryByIdResponseType, true>({
    url: `/commercial/internal/v1/skps/${skpId}/details/${params.id}`,
  })
